import * as fromCourse from './course.reducer';
import {createFeatureSelector, createSelector} from '@ngrx/store';
import * as fromSection from './course-section.reducer';
import * as fromLesson from './lesson.reducer';
import {UserState} from './user.reducer';
import {PlatformState} from './platform.reducer';
import {PricingPlansState} from './pricing-plans.reducer';
import {compareLessons} from '../common/sort-model';
import {ProtectedResourceAccessState} from './protected-resource-access.reducer';
import {isSubscriptionActive} from '../models/user.model';
import {Course} from '../models/course.model';


export const selectCoursesState = createFeatureSelector<fromCourse.State>('courses');

export const selectDescriptionsState = createFeatureSelector<fromCourse.State>('descriptions');

export const selectSectionsState = createFeatureSelector<fromSection.State>('sections');

export const selectLessonsState = createFeatureSelector<fromLesson.State>('lessons');

export const userState = createFeatureSelector<UserState>('user');

export const platformState = createFeatureSelector<PlatformState>('platform');

export const videoAccessState = createFeatureSelector<ProtectedResourceAccessState>('resourceAccess');

export const pricingPlansState = createFeatureSelector<PricingPlansState>('pricingPlans');

export const selectCourseIds = createSelector(selectCoursesState, fromCourse.selectIds);

export const selectCourseEntities = createSelector(selectCoursesState, fromCourse.selectEntities);

export const selectTenantInfo = createSelector(
  platformState,
  platformState => platformState.tenantInfo
);

export const selectAllCourses = createSelector(selectCoursesState, fromCourse.selectAll);

export const selectPublishedCourses = createSelector(
  selectAllCourses,
  courses => courses.filter(course => course.status == 'published')
);

export const selectPublishedAndEnabledCertificateCourses = createSelector(
  selectPublishedCourses,
  courses => courses.filter(course => (course.certificate && (course.certificate.disabled === false || course.certificate.disabled === undefined)) || course.certificate === undefined)
);

export const selectTotalCourses = createSelector(selectCoursesState, fromCourse.selectTotal);

export const selectAllSections = createSelector(selectSectionsState, fromSection.selectAll);

export const selectAllLessons = createSelector(selectLessonsState, fromLesson.selectAll);




export const selectInitialCoursesLoaded = createSelector(
  selectCoursesState,
  state => state.initialCoursesLoaded
);


export const selectActiveCourse = createSelector(
  selectCoursesState,
  selectAllCourses,
  (state, courses) => courses.find(course => course.id === state.activeCourseId)
);


export const selectActiveCourseDescription = createSelector(
  selectDescriptionsState,
  selectActiveCourse,
  (descriptions, course) =>  course ? descriptions[course.id] : ''
);



export const selectActiveCourseSections = createSelector(
  selectAllSections,
  selectActiveCourse,
  (sections, course) =>  {

    if(course && course?.cloneType === "read-only") {

      return sections.filter(section => section.courseId === course.sourceCourseId) || []

    }

    return course ? sections.filter(section => section.courseId === course.id) : []
  }
);


export const selectActiveCourseSectionIds = createSelector(
  selectActiveCourseSections,
  sections => sections.map(section => section.id)
);



export const selectActiveCourseAllLessons = createSelector(
  selectAllLessons,
  selectActiveCourseSectionIds,
  (lessons, sectionIds) => lessons.filter(lesson => sectionIds.includes(lesson.sectionId)).sort(compareLessons)
);

export const selectActiveCourseAllPublishedLessons = createSelector(
  selectActiveCourseAllLessons,
  (lessons) => lessons.filter(lesson => lesson.status == 'published')
);


export const selectActiveLesson = createSelector(
  selectLessonsState,
  selectActiveCourseAllLessons,
  (lessonsState, activeCourseLessons) => activeCourseLessons.find(lesson => lesson.id === lessonsState.activeLessonId)
);


export const selectActiveLessonDescription = createSelector(
  selectActiveLesson,
  selectDescriptionsState,
  (activeLesson, descriptions) => descriptions[activeLesson?.id]
);


export const selectActiveSection = createSelector(
  selectSectionsState,
  selectActiveLesson,
  (sections, activeLesson) => activeLesson ? sections.entities[activeLesson.sectionId] : undefined
);


export const selectActiveCourseTotalArticles = createSelector(
  selectActiveCourseAllPublishedLessons,
  lessons => lessons.filter(lesson => lesson.type == "text-only").length
);

export const isActiveCourseSectionsLoaded = createSelector(
  selectSectionsState,
  selectActiveCourse,
  (state, course) => course ? (state.sectionsPageInfo[course.id]?.landingPageLoaded ?? false) : false
);


export const isActiveCourseLessonsLoaded = createSelector(
  selectActiveCourse,
  selectSectionsState,
  selectLessonsState,
  (course, sectionsState, lessonsState) => {
    if (!course) return false;
    if (!(course.id in sectionsState.sectionsPageInfo)) return false;
    const targetId = course.cloneType === 'read-only' && course.sourceCourseId
      ? course.sourceCourseId : course.id;
    const allSections = Object.values(sectionsState.entities).filter(s => s?.courseId === targetId);
    if (allSections.length === 0) return sectionsState.sectionsPageInfo[course.id]?.allLoaded ?? false;
    return allSections.some(s => lessonsState.loadedSections[s.id] === true);
  }
);


export const isActiveCourseDescriptionLoaded = createSelector(
  selectDescriptionsState,
  selectActiveCourse,
  (descriptions, course) =>  course ? (course.id in descriptions) : false
);



export const isActiveCourseLoaded = createSelector(
  selectActiveCourse,
  selectSectionsState,
  selectLessonsState,
  (course, sectionsState, lessonsState) => {
    if (!course) return false;
    if (!(course.id in sectionsState.sectionsPageInfo)) return false;
    const targetId = course.cloneType === 'read-only' && course.sourceCourseId
      ? course.sourceCourseId : course.id;
    const allSections = Object.values(sectionsState.entities).filter(s => s?.courseId === targetId);
    if (allSections.length === 0) return sectionsState.sectionsPageInfo[course.id]?.allLoaded ?? false;
    return allSections.some(s => lessonsState.loadedSections[s.id] === true);
  }
);

export const selectSectionsAllLoaded = createSelector(
  selectSectionsState,
  selectActiveCourse,
  (state, course) => course ? (state.sectionsPageInfo[course.id]?.allLoaded ?? false) : false
);

export const selectLastSectionSeqNoLoaded = createSelector(
  selectSectionsState,
  selectActiveCourse,
  (state, course) => course ? (state.sectionsPageInfo[course.id]?.lastSeqNoLoaded ?? 0) : 0
);

export const selectFirstSectionSeqNoLoaded = createSelector(
  selectSectionsState,
  selectActiveCourse,
  (state, course) => {
    if (!course) return 0;
    const first = state.sectionsPageInfo[course.id]?.firstSeqNoLoaded;
    return (first !== undefined && Number.isFinite(first)) ? first : 0;
  }
);

export const selectFirstSectionIsLoaded = createSelector(
  selectSectionsState,
  selectActiveCourse,
  (state, course) => course ? (state.sectionsPageInfo[course.id]?.firstSectionLoaded ?? false) : false
);



export const isLoggedIn = createSelector(
  userState,
  auth => auth.isLoggedIn
);


export const isLoggedOut = createSelector(
  isLoggedIn,
  loggedIn => !loggedIn
);

export const userPictureUrl = createSelector(
  userState,
  auth => auth.user ? auth.user.pictureUrl : undefined
);

export const isBrandThemeLoaded = createSelector(
  platformState,
  platformState => !!platformState.brandTheme
);

export const getBrandTheme = createSelector(
  platformState,
  platformState => platformState.brandTheme
);

export const isConnectedToStripe = createSelector(
  platformState,
  settings => !!settings.tenantInfo.isConnectedToStripe

);

export const selectSessionLanguage = createSelector(
  platformState,
    state => state.sessionLanguage
);

export const selectTranslatedLanguageNamesMap = createSelector(
  platformState,
  state => {

    const translatedLanguageNamesMap: {[key:string]:string} = {};

    if (!state.translations) {
      return translatedLanguageNamesMap;
    }

    const languageCodes = Object.keys(state.translations.languages);

    for (let language of languageCodes) {
      translatedLanguageNamesMap[language] = state.translations.languages[language];
    }

    return translatedLanguageNamesMap;
  }
);

export const selectUserPermissions = createSelector(
    userState,
    authState => authState.permissions
);

export const isPermissionsLoaded = createSelector(
  userState,
  authState => authState.isPermissionsLoaded
);

export const selectUser = createSelector(
  userState,
  authState => authState.user
);

export const isSubscriberInfoMessageShown = createSelector(
  userState,
  userState => userState.subscriberInfoMessageShown
);

export const isUserProfileLoaded = createSelector(
  userState,
  authState => authState.userProfileLoaded
);

// these are the courses to which the user has access to
export const selectUserCourses = createSelector(
  selectUser,
  selectTenantInfo,
  selectCoursesState,
  (userState, tenant, coursesState) => {

    const userCoursesMap = {};

    const userGroupIds = userState.groupIds;

    // first add the courses that the user can access via groups
    if (userGroupIds?.length && tenant?.studentGroups?.length) {

      const groups = tenant.studentGroups;

      for (const groupId of userGroupIds) {

        const group = groups.find(group => group.id == groupId);

        if (group?.courseIds?.length) {

          const groupCourses = group.courseIds.map(courseId => coursesState.entities[courseId]).filter(course => !!course);

          for (const course of groupCourses) {
            userCoursesMap[course.id] = course;
          }
        }
      }

    }

    // then add the courses the user has bought directly or are free
    if (coursesState.purchasedCourses) {

      const purchasedCourses = coursesState.purchasedCourses.map(courseId => coursesState.entities[courseId]).filter(course => !!course);

      const freeCourses = Object.values(coursesState.entities).filter(course => course.free && course.status == 'published' && !course.hiddenCourseOn);

      const courses = purchasedCourses.concat(freeCourses);

      for (const course of courses) {
        userCoursesMap[course.id] = course;
      }
    }

    return Object.values(userCoursesMap) as Course[];
  }
);

export const selectUserCoursesIds = createSelector(
  selectUserCourses,
  (userCourses) => userCourses?.map(course => course.id) ?? []
);

export const canUserAccessCourse = createSelector(
  selectActiveCourse,
  selectUserCoursesIds,
  selectUser,
  (activeCourse: Course, courseIds: string[], user) => {
    if (!activeCourse) {
      return false;
    }

    const ownsCourse = !!courseIds?.find((courseId) => activeCourse.id === courseId);
    const hasSubscriptionAccess =
      isSubscriptionActive(user) && !activeCourse.excludeFromSubscription;
    const isFreeCourse = !!activeCourse.free;

    return ownsCourse || hasSubscriptionAccess || isFreeCourse;
  }
);

export const doesUserOwnCourse = createSelector(
  selectActiveCourse,
  selectUserCoursesIds,
  (activeCourse, courseIds:string[]) => !!courseIds?.find(courseId => activeCourse?.id == courseId)
);

export const isAdmin = createSelector(
  userState,
  authState =>  authState?.permissions?.isAdmin ?? false
);

export const isPlatformAdmin = createSelector(
  userState,
  authState =>  authState?.permissions?.isPlatformAdmin ?? false
);

export const isSiteOwner = createSelector(
  userState,
  platformState,
  isAdmin,
  (userState, platformState, isAdmin) => {
    return (isAdmin && platformState?.tenantInfo["email"] == userState?.user?.email) || platformState?.tenantInfo?.instructorPricingPlan == 'founder';
  }
);

export const isCourseAdmin = createSelector(
  userState,
  authState =>  authState?.permissions?.isCourseAdmin ?? false
);

export const isAnyAdmin = createSelector(
  userState,
  authState =>  (authState?.permissions?.isCourseAdmin || authState?.permissions?.isAdmin) ?? false
);

export const selectAllowedCourses = createSelector(
  userState,
  authState =>  authState?.permissions?.allowedCourses ?? []
);

export const customRoles = createSelector(
  userState,
  authState =>  authState?.permissions?.customRoles ?? null
);


export const isTeamManager = createSelector(
  userState,
  userState => userState.user ? userState.user.isTeamPlan : false
);

export const selectMaxTeamSize = createSelector(
  userState,
  userState => userState.user ? userState.user.maxTeamSize : undefined
);

export const isUserSubscribed = createSelector(
  selectUser,
  user => user && !!user?.pricingPlan && (user?.pricingPlan === "lifetime" || !user?.planEndsAt || user?.planEndsAt?.toMillis() >= new Date().getTime())
);

export const selectActiveLessonVideoAccess = createSelector(
  selectActiveLesson,
  videoAccessState,
  (lesson, accessState) => lesson ? accessState.entities[lesson.id] : undefined
);

export const isActiveLessonVideoAccessLoaded = createSelector(
  selectActiveLesson,
  videoAccessState,
  (lesson, accessState) => lesson && !!accessState.entities[lesson.id]

);

export const arePricingPlansReady = createSelector(
  pricingPlansState,
  state => !!state.monthlyPlan
);


export const selectPendingLessonsReorder = createSelector(
  selectLessonsState,
  lessonsState => lessonsState.pendingLessonReordering
);

export const selectPendingCoursesReorder = createSelector(
  selectCoursesState,
  coursesState => coursesState.pendingCoursesReordering
);

export const selectPendingSectionsReorder = createSelector(
  selectSectionsState,
  sectionsState => sectionsState.pendingSectionsReordering
);

export const selectThirdPartyScripts = createSelector(
  platformState,
  platformState => platformState?.tenantInfo?.thirdPartyScripts
);

export const selectEmailPopupScript = createSelector(
  platformState,
  platformState => platformState?.tenantInfo?.emailPopupScript
)

export const selectGoogleAnalyticsConfig = createSelector(
  platformState,
  platformState => platformState?.tenantInfo?.googleAnalyticsConfig
);

export const selectPaypalConfig = createSelector(
  platformState,
  platformState => platformState?.tenantInfo?.paypalConfig
);

export const selectFacebookConfig = createSelector(
  platformState,
 platformState => platformState?.tenantInfo?.facebookConfig
)

export const selectZapierConfig = createSelector(
  platformState,
  platformState => platformState?.tenantInfo?.zapierConfig
);

export const selectPabblyConfig = createSelector(
  platformState,
  platformState => platformState?.tenantInfo?.pabblyConfig
);

export const selectZoomConfig = createSelector(
  platformState,
  platformState => platformState?.tenantInfo?.zoomConfig
);

export const isPaypalScriptLoaded = createSelector(
  platformState,
  platformState => platformState?.paypalScriptLoadRequested
);

export const isConnectedToPaymentProcessor = createSelector(
  selectPaypalConfig,
  isConnectedToStripe,
  (paypalConfig, isConnectedToStripe) => paypalConfig?.enabled || isConnectedToStripe
)

export const isConnectedToPaypal = createSelector(
  selectPaypalConfig,
  config => !!config?.enabled
);

export const selectNewsletterContent = createSelector(
  platformState,
  platformState => platformState.tenantInfo ? platformState.tenantInfo.newsletter : undefined
);

export const selectEmailProviderSettings = createSelector(
  platformState,
  platformState => platformState.emailProvider ? platformState.emailProvider : undefined
);

export const selectTotalEmailsCollected = createSelector(
  platformState,
  platformState => platformState.tenantInfo ? platformState.tenantInfo.totalEmailsCollected : undefined
);

export const isLessonUploadOngoing = createSelector(
  selectLessonsState,
  lessonsState => lessonsState.uploadsOngoing > 0
);

export const selectUploadsOngoing = createSelector(
  selectLessonsState,
  lessonsState => lessonsState.uploadsOngoing
);

export const selectLessonFormState = (lessonId: string) => createSelector(
  selectLessonsState,
  (state) => state.temporaryFormStates?.[lessonId] || null
);